import React, { useEffect, useState } from 'react'
import { Info, Search, Link as LinkIcon, Loader2 } from 'lucide-react'
import Modal from 'ui/Modal'
import InputField from 'ui/InputField'
import { toast } from 'sonner'
import InputTextArea from 'ui/InputTextArea'
import { LAYER_TOKENS } from 'ui/layers';

type PlatformType = 'custom' | 'instagram' | 'x' | 'facebook' | 'youtube' | 'tiktok' | 'reddit';

interface ExternalMention {
    label: string;
    url: string;
    platform: PlatformType;
}

interface Props {
    onClose: () => void;
    onAdd: (mention: ExternalMention) => void;
}

const IMG_CDN = process.env.REACT_APP_IMAGE_CDN;

const PLATFORM_OPTIONS: Array<{
    type: PlatformType;
    label: string;
    icon?: string | React.ReactNode;
    placeholder: string;
    validation?: (value: string) => { isValid: boolean; error?: string };
    formatValue?: (value: string) => string;
    formatUrl?: (value: string) => string;
}> = [
    {
        type: 'custom',
        label: 'Custom URL',
        placeholder: 'https://example.com/profile',
        icon: <LinkIcon className="size-4" />,
        validation: (value) => {
            try {
                new URL(value);
                return { isValid: true };
            } catch {
                return { isValid: false, error: 'Please enter a valid URL' };
            }
        }
    },
    {
        type: 'instagram',
        label: 'Instagram',
        placeholder: 'igusername',
        icon: 'instagram_icon.png',
        formatValue: (value) => value.replace('@', ''),
        formatUrl: (value) => `https://www.instagram.com/${value}`
    },
    {
        type: 'x',
        label: 'X/Twitter',
        placeholder: 'xusername',
        icon: 'x_icon.png',
        formatValue: (value) => value.replace('@', ''),
        formatUrl: (value) => `https://x.com/${value}`
    },
    {
        type: 'facebook',
        label: 'Facebook',
        placeholder: 'https://www.facebook.com/profile',
        icon: 'facebook_icon.svg',
        validation: (value) => {
            const regex = /^https:\/\/(?:.*\.)?(?:facebook\.com|fb\.com)\/.+/i;
            return {
                isValid: regex.test(value),
                error: 'Please enter a valid Facebook URL'
            };
        }
    },
    {
        type: 'youtube',
        label: 'YouTube',
        placeholder: '@youtubeusername',
        icon: 'youtube_icon.svg',
        formatValue: (value) => value.replace('@', ''),
        formatUrl: (value) => `https://www.youtube.com/@${value}`
    },
    {
        type: 'tiktok',
        label: 'TikTok',
        placeholder: '@tiktokusername',
        icon: 'tiktok_icon_new.svg',
        formatValue: (value) => value.replace('@', ''),
        formatUrl: (value) => `https://www.tiktok.com/@${value}`
    },
    {
        type: 'reddit',
        label: 'Reddit',
        placeholder: 'u/redditusername',
        icon: 'reddit_icon.svg',
        validation: (value) => {
            const regex = /^u\/[a-zA-Z0-9_-]+$/;
            return {
                isValid: regex.test(value),
                error: 'Please enter a valid Reddit username (u/username)'
            };
        },
        formatUrl: (value) => `https://www.reddit.com/${value}`
    }
];

const ExternalMentionModal: React.FC<Props> = ({ onClose, onAdd }) => {
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedPlatform, setSelectedPlatform] = useState<PlatformType | null>(null);
    const [value, setValue] = useState('');
    const [displayLabel, setDisplayLabel] = useState('');
    const [error, setError] = useState<string | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);

    // While this modal is open, hide any tippy popovers (e.g., mention suggestions)
    useEffect(() => {
        if (typeof document !== 'undefined') {
            let styleEl = document.getElementById('eoe-hide-tippy-style');
            if (!styleEl) {
                styleEl = document.createElement('style');
                styleEl.id = 'eoe-hide-tippy-style';
                styleEl.innerText = `.eoe-hide-tippy .tippy-box{display:none!important;}`;
                document.head.appendChild(styleEl);
            }
            document.body.classList.add('eoe-hide-tippy');
        }
        return () => {
            if (typeof document !== 'undefined') {
                document.body.classList.remove('eoe-hide-tippy');
            }
        };
    }, []);

    const filteredOptions = PLATFORM_OPTIONS.filter(option => 
        option.label.toLowerCase().includes(searchQuery.toLowerCase())
    );

    const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setSearchQuery(e.target.value);
    };

    const handlePlatformSelect = (platform: PlatformType) => {
        setSelectedPlatform(platform);
        setValue('');
        setDisplayLabel('');
        setError(null);
    };

    const handleValueChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        setValue(e.target.value);
        setError(null);
        
        // Auto-generate display label if not custom
        if (selectedPlatform && selectedPlatform !== 'custom' && selectedPlatform !== 'facebook') {
            const option = PLATFORM_OPTIONS.find(opt => opt.type === selectedPlatform);
            if (option && option.formatValue) {
                setDisplayLabel(option.formatValue(e.target.value));
            } else {
                setDisplayLabel(e.target.value);
            }
        }
    };

    const handleDisplayLabelChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setDisplayLabel(e.target.value);
    };

    const handleAddMention = async () => {
        if (!selectedPlatform) return;

        const option = PLATFORM_OPTIONS.find(opt => opt.type === selectedPlatform);
        if (!option) return;

        // Validate value
        if (option.validation) {
            const { isValid, error } = option.validation(value);
            if (!isValid) {
                setError(error || 'Invalid input');
                return;
            }
        }

        // Format value and URL
        const formattedValue = option.formatValue ? option.formatValue(value) : value;
        const url = option.formatUrl ? option.formatUrl(formattedValue) : formattedValue;

        // Use custom display label or formatted value
        const finalLabel = displayLabel || formattedValue;

        if (!finalLabel) {
            setError('Display name is required');
            return;
        }

        const mention: ExternalMention = {
            label: finalLabel,
            url,
            platform: selectedPlatform
        };

        setError(null);
        setIsSubmitting(true);

        try {
            onAdd(mention);
            toast.success('External mention added successfully!');
            onClose();
        } catch (error) {
            console.error('Error adding external mention:', error);
            setError('Failed to add external mention. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const renderMainModal = () => (
        <>
            <div className="flex flex-col flex-grow space-y-4">
                <p className="text-sm text-eoe-text-sub dark:text-eoe-dark-text-sub">
                    Add an external mention to reference someone outside of Societea.
                </p>

                <div className="relative mx-1">
                    <div className="relative border border-eoe-border dark:border-eoe-dark-border rounded-full">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 size-4 text-eoe-text-meta dark:text-eoe-dark-text-meta" />
                        <input
                            type="text"
                            value={searchQuery}
                            onChange={handleSearchChange}
                            placeholder="Search platforms"
                            className="w-full pl-10 pr-4 py-2 bg-eoe-bg-hover dark:bg-eoe-dark-bg-hover rounded-full text-sm text-eoe-text dark:text-eoe-dark-text"
                        />
                    </div>
                </div>

                <div className="space-y-2">
                    {filteredOptions.map(option => (
                        <button
                            key={option.type}
                            onClick={() => handlePlatformSelect(option.type)}
                            className="w-full flex items-center justify-between p-3 rounded-lg text-sm hover:bg-eoe-bg-hover dark:hover:bg-eoe-dark-icon-hover"
                        >
                            <span className="flex flex-row items-center text-eoe-text dark:text-eoe-dark-text gap-x-2">
                                {option.type === 'custom' ? (
                                    option.icon
                                ) : (
                                    <img 
                                        className={`size-4 ${option.type === 'tiktok' || option.type === 'x' ? 'bg-white rounded-full' : ''}`} 
                                        src={`${IMG_CDN}/icons/${option.icon}`} 
                                        alt={option.label + ' icon'} 
                                    />
                                )}
                                {option.label}
                            </span>
                        </button>
                    ))}
                </div>
            </div>

            <div className="flex flex-grow flex-col pb-8 justify-end place-items-end mb-6 mt-auto space-y-2 sm:space-y-0 sm:gap-x-2 sm:flex sm:justify-end sm:flex-row sm:mb-0">
                <button
                    type="button"
                    onClick={onClose}
                    className="btn-secondary w-full sm:w-fit py-2 px-4"
                >
                    Cancel
                </button>
            </div>
        </>
    );

    const renderAddModal = () => {
        if (!selectedPlatform) return null;
        const option = PLATFORM_OPTIONS.find(opt => opt.type === selectedPlatform);
        if (!option) return null;

        return (
            <>
                <div className="flex flex-col flex-grow space-y-4">
                    <p className="text-sm text-eoe-text-sub dark:text-eoe-dark-text-sub">
                        Add external mention for {option.label}.
                    </p>

                    <div className="form-control space-y-2">
                        <InputTextArea
                            id="mentionValue"
                            name="mentionValue"
                            labelText={option.label}
                            fieldPlaceholder={option.placeholder}
                            value={value}
                            onChange={handleValueChange}
                            spellCheck={false}
                            fieldRequired
                            autoComplete="off"
                            minLength={1}
                        />
                        
                        <InputField
                            type="text"
                            id="displayLabel"
                            name="displayLabel"
                            labelText="Display name"
                            fieldPlaceholder="How to display this mention"
                            value={displayLabel}
                            onChange={handleDisplayLabelChange}
                            spellCheck={false}
                            fieldRequired
                            autoComplete="off"
                            maxLength={50}
                        />
                    </div>

                    {error && (
                        <div className="flex items-center text-warning-red dark:text-warning-red-dark text-sm">
                            <Info className="mr-1 size-4" />
                            <span>{error}</span>
                        </div>
                    )}
                </div>

                <div className="flex flex-col-reverse sm:flex-row justify-end items-stretch sm:items-center pb-8 space-y-2 space-y-reverse sm:space-y-0 sm:gap-x-2">
                    <button
                        type="button"
                        onClick={() => {
                            setSelectedPlatform(null);
                            setValue('');
                            setDisplayLabel('');
                            setError(null);
                        }}
                        className="btn-secondary w-full sm:w-fit py-2 px-4"
                    >
                        Back
                    </button>
                    <button
                        type="button"
                        onClick={handleAddMention}
                        disabled={isSubmitting || !value || !displayLabel}
                        className={`w-full sm:w-fit py-2 px-4 ${
                            isSubmitting || !value || !displayLabel ? 'btn-primary-disabled' : 'btn-primary'
                        }`}
                    >
                        {isSubmitting ? <Loader2 className="size-4 animate-spin" /> : 'Add Mention'}
                    </button>
                </div>
            </>
        );
    };

    return (
        <Modal 
            isOpen={true} 
            onClose={onClose} 
            title="External Mention"
            onBack={selectedPlatform ? () => {
                setSelectedPlatform(null);
                setValue('');
                setDisplayLabel('');
                setError(null);
            } : undefined}
            layerBase={LAYER_TOKENS.previewSurface}
            disableOutsideClose={true}
        >
            <div className="flex flex-col w-full sm:h-auto overflow-y-scroll sm:overflow-y-auto space-y-4">
                {selectedPlatform ? renderAddModal() : renderMainModal()}
            </div>
        </Modal>
    );
};

export default ExternalMentionModal; 